import React, { useEffect, useState } from 'react';
import {
  Box, Card, Typography, Grid, TextField, Switch, FormControlLabel, Button,
  CircularProgress, Alert, Snackbar, Stack, IconButton, Divider,
} from '@mui/material';
import { Plus, Trash2, Save } from 'lucide-react';
import { fetchPartnerSettings, updatePartnerSettings } from '../utils/partnerApi';
import PageHeader from '../components/common/PageHeader';

const PartnerSettingsView = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchPartnerSettings();
        setSettings(res.data);
      } catch (e) {
        notify('Failed to load settings', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
  const setTier = (i, k, v) => setSettings((s) => {
    const tiers = [...(s.monthly_bonus_tiers || [])];
    tiers[i] = { ...tiers[i], [k]: Number(v) };
    return { ...s, monthly_bonus_tiers: tiers };
  });
  const addTier = () => setSettings((s) => ({ ...s, monthly_bonus_tiers: [...(s.monthly_bonus_tiers || []), { paid_sellers: 0, bonus_amount: 0 }] }));
  const removeTier = (i) => setSettings((s) => ({ ...s, monthly_bonus_tiers: s.monthly_bonus_tiers.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        program_enabled: settings.program_enabled,
        global_commission_percent: Number(settings.global_commission_percent),
        min_payout_amount: Number(settings.min_payout_amount),
        require_kyc_for_earning: settings.require_kyc_for_earning,
        require_kyc_for_payout: settings.require_kyc_for_payout,
        top_partner_reward_amount: Number(settings.top_partner_reward_amount),
        monthly_bonus_tiers: (settings.monthly_bonus_tiers || [])
          .filter((t) => t.paid_sellers > 0)
          .map((t) => ({ paid_sellers: Number(t.paid_sellers), bonus_amount: Number(t.bonus_amount) })),
      };
      const res = await updatePartnerSettings(body);
      setSettings(res.data.settings);
      notify('Settings saved');
    } catch (e) {
      notify('Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (!settings) return <Alert severity="error" sx={{ m: 2 }}>Could not load settings.</Alert>;

  return (
    <Box sx={{ py: 1, maxWidth: 840 }}>
      <PageHeader
        title="Partner Program Settings"
        subtitle="Configure default commissions, payout thresholds, and monthly seller bonus rewards."
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Growth', href: '/partners' },
          { label: 'Settings' }
        ]}
        actionLabel={saving ? "Saving..." : "Save Settings"}
        actionIcon={<Save size={18} />}
        onAction={save}
      />

      <Card sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <FormControlLabel
          control={<Switch checked={!!settings.program_enabled} onChange={(e) => set('program_enabled', e.target.checked)} />}
          label={<Typography sx={{ fontWeight: 700 }}>Program enabled (master kill switch — off = no commissions accrue)</Typography>}
        />
        <Divider sx={{ my: 2 }} />
        <Grid container spacing={2.5}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField fullWidth type="number" label="Global commission %" helperText="Fallback when a plan has no fixed ₹ commission"
              value={settings.global_commission_percent} onChange={(e) => set('global_commission_percent', e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField fullWidth type="number" label="Minimum payout amount (₹)"
              value={settings.min_payout_amount} onChange={(e) => set('min_payout_amount', e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControlLabel control={<Switch checked={!!settings.require_kyc_for_earning} onChange={(e) => set('require_kyc_for_earning', e.target.checked)} />}
              label="Require KYC approval before earning" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControlLabel control={<Switch checked={!!settings.require_kyc_for_payout} onChange={(e) => set('require_kyc_for_payout', e.target.checked)} />}
              label="Require KYC approval before payout" />
          </Grid>
        </Grid>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Tip: set a fixed ₹ commission per plan on the Plans page (field "partner_commission") to override the global %.
        </Typography>
      </Card>

      <Card sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Monthly Volume Bonuses</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Award a bonus when a partner reaches a paid-seller milestone in a calendar month. The highest matching tier is paid.
        </Typography>
        <Stack spacing={1.5}>
          {(settings.monthly_bonus_tiers || []).map((t, i) => (
            <Stack key={i} direction="row" spacing={1.5} alignItems="center">
              <TextField size="small" type="number" label="Paid sellers" value={t.paid_sellers} onChange={(e) => setTier(i, 'paid_sellers', e.target.value)} sx={{ width: 150 }} />
              <TextField size="small" type="number" label="Bonus (₹)" value={t.bonus_amount} onChange={(e) => setTier(i, 'bonus_amount', e.target.value)} sx={{ width: 150 }} />
              <IconButton aria-label="Remove tier" color="error" onClick={() => removeTier(i)}><Trash2 size={18} /></IconButton>
            </Stack>
          ))}
        </Stack>
        <Button startIcon={<Plus size={16} />} onClick={addTier} sx={{ mt: 1.5, textTransform: 'none' }}>Add Tier</Button>
        <Divider sx={{ my: 2 }} />
        <TextField type="number" label="Top Partner of the Month reward (₹)" value={settings.top_partner_reward_amount}
          onChange={(e) => set('top_partner_reward_amount', e.target.value)} sx={{ width: 280 }} />
      </Card>

      <Button variant="contained" startIcon={<Save size={18} />} disabled={saving} onClick={save}
        sx={{ textTransform: 'none', fontWeight: 700, px: 4, py: 1.2 }}>
        {saving ? 'Saving…' : 'Save Settings'}
      </Button>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ fontWeight: 600 }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PartnerSettingsView;
